import React, { Component, Fragment, ReactNode } from 'react';
import { RootState } from '../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { setPermissions, setUser } from '../redux/actions/UserActions';
import HttpClient from '../gateway/HttpClient';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import { MenuPermission, User } from '../domain/rbac/RbacDomains';
import HttpMethod from '../constant/HttpMethod';
import { UserMethods } from '../constant/MethodNames';
import { changeLanguage } from '../redux/actions/PageActions';
import CustomLoading from '../components/custom/CustomLoading';
import { IntlProvider } from 'react-intl';
import English from '../config/multilang/languages/en.json';
import Turkish from '../config/multilang/languages/tr.json';
import { Toast } from 'primereact/toast';
import MessageUtil from '../util/MessageUtil';
import KeycloakStore from '../config/KeycloakStore';
import RoleHelper from '../RoleHelper';
import loader from '@monaco-editor/loader';
import { Socket } from 'socket.io-client';
import { InstanceContainer } from '../config/binding/InstanceBinding';
import Injectables from '../constant/Injectables';
import { UiPackI18nProvider } from '@trlogic/ui-pack';
import Uzbek from '../config/multilang/languages/uzbek.json';
import Russian from '../config/multilang/languages/ru.json';

class Loader extends Component<Props, State> {
  public state: State = new State();

  private httpClient: HttpClient = GatewayContainer.getInstance().get(GatewayNames.API_CLIENT);
  private readonly socket: Socket = InstanceContainer.get(Injectables.SOCKET_CLIENT);

  public render(): ReactNode {
    const { children, language } = this.props;
    const { loading } = this.state;
    if (loading) {
      return <CustomLoading />;
    }

    const messages = this.flattenMessages(this.getLanguageMessages());
    return (
      <Fragment>
        <Toast ref={(ref) => ref && MessageUtil.setMessageModule(ref)} className='!z-[9999]' />
        <IntlProvider messages={messages} locale={language} defaultLocale='en-US'>
          <UiPackI18nProvider
            locale={language}
            t={(key: string, defaultText?: string, _options?: Record<string, any>): string => {
              const value = messages[key as keyof typeof messages];
              if (typeof value === 'string') {
                return value;
              }
              return defaultText ?? key;
            }}
          >
            {children}
          </UiPackI18nProvider>
        </IntlProvider>
      </Fragment>
    );
  }

  public async componentDidMount() {
    loader.config({
      paths: {
        vs: '/vs',
      },
    });
    await loader.init();
    this.build();
    this.socket.on(`user-update`, () => {
      this.refreshUser();
    });
  }

  private refreshUser = async () => {
    const { setUser, setPermissions } = this.props;
    const userInstance: User | undefined = await this.httpClient.execute<User>(HttpMethod.GET, UserMethods.GET);
    if (userInstance != undefined) {
      try {
        const permissions: MenuPermission[] = await this.httpClient.execute<MenuPermission[]>(HttpMethod.GET, UserMethods.CALCULATE_ROLES, []);
        setPermissions(permissions);
        setUser(userInstance);
        RoleHelper.setRoles(permissions);
        this.refreshFontSize(userInstance);
      } catch (e) {
        setUser(null);
        KeycloakStore.getKeycloakInstance()?.logout();
        this.setState({ loading: false });
        console.error(e);
      }
    } else {
      this.setState({ loading: false });
      setUser(null);
      KeycloakStore.getKeycloakInstance()?.logout();
    }
  };

  private build = () => {
    const { user } = this.props;
    if (user != undefined) {
      this.initialize();
    }
  };

  private refreshFontSize = (user: User) => {
    const fontSize = user.preferences.fontSize;
    if (fontSize) {
      const htmlElement = document.getElementsByTagName('html')[0];
      htmlElement.style.fontSize = fontSize;
    }
  };

  private initialize = async () => {
    const { user } = this.props;
    if (user == undefined) return;
    await this.refreshUser();
    this.setState({ loading: false });
  };

  private flattenMessages = (nestedMessages: Record<string, any>, prefix = '') => {
    if (nestedMessages === null) {
      return {};
    }
    return Object.keys(nestedMessages).reduce((messages, key) => {
      const value = nestedMessages[key];
      const prefixedKey = prefix ? `${prefix}.${key}` : key;

      if (typeof value === 'string') {
        Object.assign(messages, { [prefixedKey]: value });
      } else {
        Object.assign(messages, this.flattenMessages(value, prefixedKey));
      }

      return messages;
    }, {});
  };

  private getLanguageMessages = (): Record<string, any> => {
    const { language } = this.props;
    switch (language) {
      case'ru-RU':
        return Russian;
      case 'uzbek':
        return Uzbek;
      case 'tr-TR':
        return Turkish;
      default:
        return English;
    }
  };
}

interface Props extends ReduxProps {
  children: any;
}

class State {
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  collapsed: state.page.sidebarCollapsed,
  user: state.auth.user as User,
  language: state.page.language,
});

const mapDispatchToProps = { setUser, setPermissions, changeLanguage };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(Loader);
